import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { VerificationBadge, AfCFTABadge } from './Badges';
import { MapPin, Package, DollarSign } from 'lucide-react';

export const ProductCard = ({ product }) => {
  const {
    product_id,
    name,
    description,
    price,
    currency,
    country_of_origin,
    category,
    moq,
    images,
    seller_name,
    verification_status,
    afcfta_eligible,
  } = product;

  // Use placeholder if no image or image is from example.com (broken)
  const rawImageUrl = images?.[0];
  const isValidImage = rawImageUrl && !rawImageUrl.includes('example.com');
  const imageUrl = isValidImage 
    ? rawImageUrl 
    : 'https://images.unsplash.com/photo-1768212565426-58b089b6386d?w=400&h=300&fit=crop';

  const [imgError, setImgError] = React.useState(false);
  const displayImage = imgError 
    ? 'https://images.unsplash.com/photo-1768212565426-58b089b6386d?w=400&h=300&fit=crop'
    : imageUrl;

  return (
    <Link to={`/products/${product_id}`} data-testid={`product-card-${product_id}`}>
      <Card className="card-interactive overflow-hidden h-full">
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
          <img
            src={displayImage}
            alt={name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={() => setImgError(true)}
          />
          {afcfta_eligible && (
            <div className="absolute top-3 left-3">
              <AfCFTABadge eligible={true} size="small" />
            </div>
          )}
        </div>

        <CardContent className="p-4">
          {/* Category */}
          <Badge variant="secondary" className="mb-2 text-xs capitalize">
            {category}
          </Badge>

          {/* Title */}
          <h3 className="font-poppins font-semibold text-charcoal line-clamp-2 mb-2 group-hover:text-continental transition-colors">
            {name}
          </h3>

          {/* Description */}
          <p className="text-sm text-gray-500 line-clamp-2 mb-3">{description}</p>

          {/* Price & MOQ */}
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-xl font-bold text-continental">
              {currency} {price.toLocaleString()}
            </span>
            <span className="text-sm text-gray-400">/ unit</span>
          </div>

          <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-3">
            <Package className="w-4 h-4" />
            <span>MOQ: {moq} units</span>
          </div>

          {/* Seller & Location */}
          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-charcoal">{seller_name}</p>
                <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                  <MapPin className="w-3 h-3" />
                  {country_of_origin}
                </div>
              </div>
              <VerificationBadge status={verification_status} size="small" />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default ProductCard;
